import { HttpClient, HttpEventType, HttpHeaders, HttpParams, HttpProgressEvent, HttpResponse } from '@angular/common/http';
import { DOCUMENT, inject, Injectable } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NexusApiService } from '@df/api/nexus/nexus-api.service';
import { Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { CacheService } from '@df/core/cache/cache';
import { HeadService } from '@df/core/meta/head/head.service';
import { Logger } from '@df/core/service/logger';
import { Status } from '@df/core/status';
import { IScrollToOptions } from '@df/dom/scroll/scroll-to.service';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { ScrollToErrorService } from '@df/ui/form/scroll-to-error.service';
import { Nexus } from '@tomandco/nexus-api-types';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import flatten from 'lodash-es/flatten';
import forEach from 'lodash-es/forEach';
import isArray from 'lodash-es/isArray';
import isFunction from 'lodash-es/isFunction';
import isNumber from 'lodash-es/isNumber';
import isObject from 'lodash-es/isObject';
import isString from 'lodash-es/isString';
import keyBy from 'lodash-es/keyBy';
import pickBy from 'lodash-es/pickBy';
import union from 'lodash-es/union';
import { Subject, Subscription } from 'rxjs';
import { NDfApi } from '../df-api.interfaces';

declare global {
    export interface Promise<T> {
        cancelled?: boolean;
        cancel?: () => void;
    }
}

declare module '@angular/forms' {
    interface NgForm {
        submitAttempt: boolean;
    }
}

export interface IFindRequestParams {
    verbosity: number;
    limit?: number;
    skip?: number;
    sort?: object;
    filter: object;
    store?: number;
    pushDeps?: boolean;
}
export interface IFindRequest extends IFindRequestParams {
    type: string;
}

interface IPromiseMethodWrap<T> {
    promise: Promise<T>;
    resolve: (value: T) => void;
    reject: (reason?: any) => void;
}
export interface IScriptLoadOptions<T extends HTMLScriptElement | HTMLLinkElement = HTMLScriptElement | HTMLLinkElement> {
    onScript?: (script: T) => void;
    onLoad?: (script: T) => void;
    onError?: (script: T) => void;
    reload?: boolean;
    cleanup?: boolean;
    scriptType?: string;
}

export interface ICategoryProductsRequest {
    category: number;
    limit?: number;
    verbosity: number;
    ingoreStock?: boolean;
}

export interface ICallableObject<T = any> {
    path: string;
    data?: T;
    api?: IApiType; // defaults to php
    // if token is required
    token?: boolean;
    requestOptions?: IRequestOptions;
}

export type IApiType = 'node' | 'php' | 'ssr';

export interface ICallableObjectFn {
    fn: (data?: any) => any;
    data: any;
}

export declare type ICallableFunction = (...args) => any;

export declare type ICallable = ICallableObject | ICallableObjectFn | ICallableFunction;

export interface IRequestOptions {
    method?: 'GET' | 'POST' | 'PUT';
    endpoint?: string;
    headers?: { [name: string]: string | string[] };
    responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
    onProgress?: (event: HttpProgressEvent) => void;
    fileUpload?: boolean;
    withCredentials?: boolean;
    params?: { [key: string]: any };
    api?: IApiType; // defaults to php; may be passed through callbale too
    token?: boolean | string; // if token is required flag (icms token will be loaded)
}

export declare type IRequestCallbackFunction<T = any> = (result?: T) => any | void;

export declare type IRequestCallback<T = any> = IRequestCallbackFunction<T> | IRequestCallbackFunction<T>[];

export interface IBundleResolver {
    resolve: IResolve;
    reject: IReject;
    onSuccess?: IRequestCallback;
    onError?: IRequestCallback;
    resolved?: boolean;
}

export declare type IResolve = (value?: any | PromiseLike<any>) => void;
export declare type IReject = (reason?: any) => void;

export interface ILoadModelRequestParams {
    type: string;
    id: number | string;
    verbosity: number;
}

export interface IResponseMeta<TResult = any> {
    result: TResult;
    promises?: Promise<any>[];
    request?: ICallable;
}

export interface IResponseMetaResult {
    post_redirect?: IResponseMetaResultRedirect;
    external_redirect?: string;
    session?: IResponseMetaResultSession;
}

export interface IResponseMetaResultRedirect {
    action: string;
    data: any;
    modal?: boolean;
}

export interface IResponseMetaResultSession {
    message?: string;
}

export interface ILoadedAssets {
    [key: string]: ILoadedAssetsGroup;
}

export interface ILoadedAssetsGroup {
    onSuccess: ICallableFunction[];
    onError: ICallableFunction[];
    loaded: boolean;
    loading: boolean;
    target?: HTMLScriptElement | HTMLLinkElement;
}

// removes onSuccess and onError callbacks for load assets
export type CancelLoadAssetsCallbacks = () => void;

export enum ResourceType {
    Script,
    Stylesheet
}

export interface IPrioritizedRequestServiceSubscription {
    subscription: Subscription;
    priority: number;
}

export interface IMetaCallRequest {
    /**
     * Filter to apply to collection query
     * @type {any}
     */
    filter: any;
}

export interface IMetaCallResponse {
    count: number;
}

export interface ISubmitFormOptions {
    errorScrollContainer?: HTMLElement;
    errorScrollOptions?: IScrollToOptions;
}

export interface ISerializeObject {
    [key: string]: string | number | boolean;
}

export interface IRequestSubjectEvent<T = any, TResponse = any> {
    path: string;
    data: any;
    promise?: Promise<T>;
    options: IRequestOptions;
    status: 'start' | 'success' | 'error' | 'cancelled';
    error?: string;
    responseData?: TResponse;
}

@Injectable({
    providedIn: 'root'
})
export class RequestService {
    /**
     * Model types that should not push dependencies when loadinf
     */
    static readonly preventPushingDepsFor: string[] = [];

    /**
     * Enforce no cache
     */
    static enforceNoCache = false;

    /**
     * Response Subject
     * @type {Subject}
     *
     * @deprecated Use dfEvent with id dfApiRequest instead
     */
    private subject = new Subject<IResponseMeta>();

    /**
     * Request Subject
     * @deprecated Use dfEvent with id dfApiRequest instead
     */
    requestSubject = new Subject<IRequestSubjectEvent>();

    private prioritizedSubscriptions: IPrioritizedRequestServiceSubscription[] = [];

    /**
     * Buffer table for loaded assets to prevent double loading
     * @type {string[]}
     */
    private loadedAssets: ILoadedAssets = {};

    private alreadyTrackedObjects = new Set<any>();

    @NgInject(NexusApiService)
    readonly nexus!: NexusApiService;

    @NgInject(CacheService)
    readonly cacheService!: CacheService;

    private app = inject(App);
    private headService = inject(HeadService);
    private document = inject(DOCUMENT);
    private logger = inject(Logger);
    private http = inject(HttpClient);
    readonly timeoutService = inject(TimeoutService);
    readonly scrollToErrorService = inject(ScrollToErrorService);

    constructor() {
        this.app.request = this;

        // normalize endpoint paths
        ['nodeApi', 'ssrApi', 'mage'].forEach(api => {
            if (!this.app.dfConfig.endpoint[api].endsWith('/')) {
                this.app.dfConfig.endpoint[api] += '/';
            }
        });
    }

    /**
     * Wraps request to support using status and promise models
     *
     * @see _promise;
     * @see _status;
     * @see _constat;
     *
     * @param  {int | object} status        Request status as defined in `constat` or status model
     * @param  {object | function} callable      Either function that will return promise or
     * {path : string, data: object} or {fn : function, data: object}
     * @param  {function | function[]} onSuccess  On request success callback
     * @param  {function | function[]} onError    On request error callback
     * @return {promise}             Request promise (can be accessed via getPromsie)
     */
    send<T = any>(
        status: Status | undefined,
        callable: ICallable,
        onSuccess?: IRequestCallback<T>,
        onError?: IRequestCallback,
        requestOptions: IRequestOptions = {}
    ): Promise<T> {
        if (status) {
            // cancel current request
            if (status.busy && status.promise && status.promise.cancel) {
                status.promise.cancel();
            }
            // update status
            status.busy = true;
        }
        let cancel;
        const promise = new Promise<T>((resolve, reject) => {
            let innerPromise;
            // send request
            try {
                if (isFunction(callable)) {
                    innerPromise = (<ICallableFunction>callable)();
                } else {
                    if ((<ICallableObjectFn>callable).fn) {
                        innerPromise = (<ICallableObjectFn>callable).fn((<ICallableObjectFn>callable).data);
                    } else {
                        if (!(<ICallableObject>callable).path) {
                            throw new Error('Missing path param for request');
                        }
                        if ((<ICallableObject>callable).api) {
                            requestOptions.api = (<ICallableObject>callable).api;
                        }
                        if ((<ICallableObject>callable).token) {
                            requestOptions.token = (<ICallableObject>callable).token;
                        }

                        innerPromise = this.sendXHR((<ICallableObject>callable).path, (<ICallableObject>callable).data, {
                            ...requestOptions,
                            ...(<ICallableObject>callable).requestOptions
                        });
                    }
                }
            } catch (err) {
                if (status) {
                    status.error = err as any;
                }
                reject({ error: err });
                return;
            }
            // if no promise then return nothing;
            if (!innerPromise || !(innerPromise instanceof Promise)) {
                if (status) {
                    status.success = 'SUCCESS';
                }
                resolve(null as T);
                return;
            }
            cancel = this.process(status, callable, innerPromise, resolve, reject, onSuccess, onError);
        });
        promise.catch(() => {});
        promise.cancel = () => {
            promise.cancelled = true;
            cancel();
        };
        if (status) {
            status.promise = promise;
        }
        return promise;
    }

    /**
     * Sends XHR (get or post, post by default) to core API
     *
     * interface option
     * hod : string,
     *     responseType ?: string,
     *     fileUpload : bool
     * }
     *
     * @param  {string} path    API path
     * @param  {any} data (optional)
     * @param  {options|string} options  (optionsl)
     * @return {promise}
     */
    sendXHR<T>(path: string, data: { [key: string]: any } | undefined, options: IRequestOptions = {}): Promise<T> {
        // normalize options
        const isPost = !options.method || options.method === 'POST';

        const isAbsolute = path.match(/https?:\/\//);
        const api = options.api || 'php';

        // normalize path
        if (!isAbsolute) {
            if (path.startsWith('/')) {
                path = path.slice(1);
            }
            if (this.app.dfConfig.integration.apiMode === 'direct' && api === 'php' && !options.endpoint) {
                if (!['rest/', 'static/', 'store-static/', 'media/'].some(prefix => path.startsWith(prefix))) {
                    path = `coreapi/${path}`;
                }
            }
        }
        let url: string;
        if (isAbsolute) {
            url = path;
        } else {
            let domain: string;
            if (options.endpoint) {
                domain = options.endpoint;
            } else {
                switch (api) {
                    case 'node':
                        domain = this.app.dfConfig.endpoint.nodeApi;
                        break;
                    case 'ssr':
                        domain = this.app.dfConfig.endpoint.ssrApi;
                        break;
                    default:
                        if (this.app.dfConfig.integration.apiMode === 'direct') {
                            domain = this.app.dfConfig.endpoint.mage;
                        } else {
                            domain = this.app.dfConfig.endpoint.phpApi;
                        }
                        break;
                }
            }
            url = domain + path;
        }

        const params = new HttpParams({ fromObject: options.params });

        const headers: { [name: string]: string | string[] } = options.headers || {};
        if (options.fileUpload) {
            const formData = new FormData();
            appendFormdata(formData, data);
            data = formData;
        } else if (isPost) {
            headers['Content-Type'] = 'application/json';
        }
        const next = () => {
            let cleanup: Subscription;
            const eventData: NDfApi.IApiRequestEventData = {
                url,
                path,
                requestData: data,
                method: isPost ? 'POST' : 'GET'
            };

            const event: IRequestSubjectEvent<T> = { path, data, promise: undefined, options, status: 'start' };
            const promise = this.app.event.dispatch<T>({
                id: 'dfApiRequest',
                data: eventData,
                action: eventData => {
                    if (!isPost && data) {
                        eventData.url += (url.indexOf('?') > -1 ? '&' : '?') + this.serialize(data);
                    }

                    const observe = options.onProgress ? 'events' : undefined;
                    const observable = this.http.request(eventData.method, eventData.url, {
                        body: isPost ? eventData.requestData : undefined,
                        headers: new HttpHeaders(headers),
                        reportProgress: options.onProgress ? true : false,
                        observe: observe,
                        responseType: options.responseType,
                        params,
                        withCredentials: typeof options.withCredentials === 'undefined' ? api === 'php' : options.withCredentials
                    });

                    return new Promise<T>((resolve, reject) => {
                        let _hasValue = false;
                        let _value: T;
                        cleanup = observable.subscribe({
                            next: event => {
                                if (observe) {
                                    if (options.onProgress) {
                                        options.onProgress(event as HttpProgressEvent);
                                    }
                                    if (event.type === HttpEventType.Response) {
                                        _hasValue = true;
                                        _value = (event as HttpResponse<T>).body as T;
                                    }
                                } else {
                                    _hasValue = true;
                                    _value = event as T;
                                }
                            },
                            error: reject,
                            complete: () => {
                                if (_hasValue) {
                                    if ((_value as any)?.error) {
                                        reject(_value);
                                    } else if (isString(_value)) {
                                        reject();
                                    } else {
                                        resolve(_value as T);
                                        event.status = 'success';
                                        this.requestSubject.next(event);
                                    }
                                } else {
                                    reject();
                                }
                            }
                        });
                    });
                }
            });
            event.promise = promise;
            promise.catch(e => {
                event.status = 'error';
                event.error = typeof e === 'object' ? e.message || e.error : e;
                event.responseData = e;
                this.requestSubject.next(event);
            });

            promise.cancel = () => {
                promise.cancelled = true;
                cleanup?.unsubscribe();
                event.status = 'cancelled';
                this.requestSubject.next(event);
            };

            this.requestSubject.next(event);

            return promise as Promise<T>;
        };

        let isNoCdnCache = (typeof document !== 'undefined' && document.cookie.includes('cdnCacheOff')) || RequestService.enforceNoCache;
        let isNoApiCache = (typeof document !== 'undefined' && document.cookie.includes('apiCacheOff')) || RequestService.enforceNoCache;
        if (!this.app.isPlatformBrowser) {
            headers['X-App-Id'] = 'ssr';
            isNoCdnCache = true;
        } else if (this.app.client.isPrerender) {
            headers['X-App-Id'] = 'prerender';
            isNoCdnCache = true;
        } else if (this.app.client && (this.app.client.isCms || this.app.client.forceCmsRequests) && options.api === 'node') {
            headers['X-App-Id'] = 'cms';
            isNoCdnCache = true;
            isNoApiCache = true;
        } else if (this.app.acl.check('cms')) {
            isNoCdnCache = true;
            isNoApiCache = true;
        }

        if (options.api === 'node') {
            headers['X-Store-Id'] = this.app.dfConfig.ms.store + '';
            if (isNoCdnCache) {
                headers['Cache-Control'] = 'no-cache';
            }
            if (isNoApiCache) {
                headers['X-Cache-Control'] = 'no-cache';
            }
        }
        if (options.token === true) {
            let cancel: () => void;
            let wrappedPromise: Promise<T>;
            // eslint-disable-next-line prefer-const
            wrappedPromise = new Promise<T>((resolve, reject) => {
                this.app.acl.prepareToken(token => {
                    if (typeof wrappedPromise !== 'undefined' && wrappedPromise.cancelled) {
                        return;
                    }
                    if (!token) {
                        reject({ error: 'Could not retrieve access token. Please make sure you are signed in admin area.' });
                    } else {
                        headers['X-Authorization'] = 'Bearer ' + token;
                        const inPromise = next();
                        inPromise
                            .then(result => {
                                if (
                                    typeof result === 'object' &&
                                    typeof (result as any).error === 'string' &&
                                    (result as any).error.match('token')
                                ) {
                                    this.app.acl.deactivateToken();
                                }
                                return resolve(result);
                            })
                            .catch(reject);
                        cancel = () => inPromise.cancel?.();
                    }
                });
            });
            wrappedPromise.cancel = () => {
                wrappedPromise.cancelled = true;
                if (cancel) {
                    cancel();
                }
            };

            // just not to get unhandled promise rejection
            wrappedPromise.catch(() => {});

            return wrappedPromise;
        } else {
            if (typeof options.token === 'string') {
                headers['X-Authorization'] = 'Bearer ' + options.token;
            }
            return next();
        }

        function appendFormdata(formData: FormData, requestData: any, name?: string) {
            if (typeof requestData === 'object' && !(requestData instanceof Blob)) {
                forEach(requestData, (value, index) => {
                    if (!name) {
                        appendFormdata(formData, value, '' + index);
                    } else {
                        appendFormdata(formData, value, name + '[' + index + ']');
                    }
                });
            } else if (name && requestData) {
                formData.append(name, requestData);
            }
        }
    }

    /**
     * promice can be promise result
     * @param  {[type]} status    [description]
     * @param  {[type]} promise   [description]
     * @param  {[type]} resolve   [description]
     * @param  {[type]} reject    [description]
     * @param  {[type]} onSuccess [description]
     * @param  {[type]} onError   [description]
     * @return {[type]}           [description]
     */
    process(
        status: Status | undefined,
        callable: ICallable,
        promise: Promise<any>,
        resolve: IResolve,
        reject: IReject,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): void | (() => void) {
        if (promise instanceof Promise) {
            promise
                .then(result => {
                    this.timeoutService.setTimeout(() =>
                        this.onWrappedSuccess(result, status, callable, promise, resolve, reject, onSuccess, onError)
                    );
                })
                .catch(result => {
                    if (result instanceof Error) {
                        console.error(result);
                    }
                    this.timeoutService.setTimeout(() => this.onWrappedError(result, status, callable, promise, reject, onError));
                });
            // bind cancel function (not sure what that code was about cause it was never used)
            return () => {
                if (promise.cancel) {
                    promise.cancel();
                }
                promise.cancelled = true;
                reject();
            };
        } else {
            this.onWrappedSuccess({ data: promise }, status, callable, promise, resolve, reject, onSuccess, onError);
        }
    }

    private onWrappedSuccess(
        plainResult,
        status: Status | undefined,
        callable: ICallable,
        promise: Promise<any>,
        resolve: IResolve,
        reject: IReject,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): void {
        // do nothing if that was intentional cancellation
        if (promise?.cancelled) {
            return;
        }
        let canSendErrorEvent = true;
        try {
            let result = plainResult ? plainResult.data || plainResult || {} : {};
            if (result?.error) {
                throw result;
            }
            if (isString(result)) {
                throw 1;
            }

            const subjectData: IResponseMeta = {
                result,
                promises: [],
                request: callable
            };

            try {
                canSendErrorEvent = false;
                if (!this.alreadyTrackedObjects.has(result)) {
                    this.alreadyTrackedObjects.add(result);
                    this.timeoutService.setTimeout(() => {
                        this.alreadyTrackedObjects.delete(result);
                    }, 2000);
                    this.subject.next(subjectData);
                }
            } catch (err) {
                this.logger.error(err);
            }

            const promises = subjectData.promises ? [...subjectData.promises] : [];
            // retest against errors in case we one got passed in subscrbed methods
            if (result?.error) {
                throw result;
            }

            if (onSuccess) {
                forEach(flatten(isArray(onSuccess) ? onSuccess : [onSuccess]), onSuccessFunction => {
                    if (onSuccessFunction) {
                        const onSuccessResult = onSuccessFunction(result);
                        // if onSuccess function returned any result then ..
                        if (onSuccessResult !== undefined) {
                            // .. if that result is promise ..
                            if (onSuccessResult instanceof Promise) {
                                // .. then add it to promises list
                                promises.push(onSuccessResult);
                            } else {
                                // .. otherwise replace result whith what was returned
                                result = onSuccessResult;
                            }
                        }
                    }
                });
            }
            if (status) {
                status.success = result?.successMessage || 'SUCCESS';
            }
            if (promises.length) {
                Promise.all(promises)
                    .then(() => this.timeoutService.setTimeout(() => resolve(result))) // we pass transformed result here!
                    .catch(err =>
                        this.timeoutService.setTimeout(() => this.onWrappedError(err, status, callable, promise, reject, onError))
                    );
            } else {
                resolve(result); // we pass transformed result here!
            }
        } catch (err) {
            this.onWrappedError(err as { error: any; status: any } | Error, status, callable, promise, reject, onError, canSendErrorEvent);
        }
    }

    private onWrappedError(
        result: { error: any; status: any } | Error,
        status: Status | undefined,
        callable: ICallable,
        promise: Promise<any>,
        reject: IReject,
        onError?: IRequestCallback,
        sendEvent = true
    ): void {
        if (isObject(result) && !(result instanceof Error) && !result.error && isNumber(result.status) && result.status > 299) {
            result.error = result.status;
        }

        if (sendEvent && isObject(result) && Object.keys(result).length > 1) {
            const subjectData: IResponseMeta = {
                result,
                request: callable
            };

            this.subject.next(subjectData);
        }

        // do nothing if that was intentional cancellation
        if (promise?.cancelled) {
            return;
        }
        if (onError) {
            if (isArray(onError)) {
                forEach(compact(flatten(onError)), onErrorFunction => {
                    result = onErrorFunction(result) || result;
                });
            } else {
                result = (<IRequestCallbackFunction>onError)(result) || result;
            }
        }

        let error;
        if (result instanceof Error) {
            error = result.message;
        } else if (isObject(result) && (isString(result.error) || isNumber(result.error))) {
            error = result.error;
        }
        if (status) {
            status.error = error;
            status.errorData = result;
        }

        typeof result === 'object' ? reject({ ...result, error }) : reject({ error }); // see that we may need to use result for status in wrapping send
    }

    /**
     *
     * @param  {[type]} IFindRequest [description]
     * @param  {[type]} onSuccess         [description]
     * @param  {[type]} onError           [description]
     * @return {[type]}                   [description]
     */
    async findModels<I extends string | number = string | number>(findRequest: IFindRequest): Promise<I[]> {
        const data: Nexus.Api.Catalog.Find.Request = {
            ...pickBy(findRequest, Boolean),
            verbosity: findRequest.verbosity,
            type: findRequest.type,
            sort: findRequest.sort ? (JSON.stringify(findRequest.sort) as any) : undefined,
            filter: JSON.stringify(findRequest.filter || {}) as any // it needs to be string
        };
        if (findRequest.pushDeps) {
            data.pushDeps = findRequest.pushDeps;
        }
        const response = await this.send<Nexus.Api.Catalog.Find.Response>(
            undefined,
            {
                path: 'find',
                data
            },
            undefined,
            undefined,
            { method: 'GET', api: 'node' }
        );

        return response.result?.[findRequest.type] || [];
    }

    /**
     *
     * @param  {[type]} IFindRequest [description]
     * @param  {[type]} onSuccess         [description]
     * @param  {[type]} onError           [description]
     * @return {[type]}                   [description]
     */
    loadCategoryProducts(requestParams: ICategoryProductsRequest, options?: { noCache: boolean }): Promise<Product[]> {
        return this.cacheService.cacheRequest(
            ['loadCategoryProducts', requestParams],
            async () => {
                const res = await this.send<{ result: { products: number[] } }>(
                    undefined,
                    {
                        path: '/categoryProducts',
                        data: {
                            category: requestParams.category,
                            limit: requestParams.limit,
                            verbosity: requestParams.verbosity,
                            ingoreStock: requestParams.ingoreStock
                        }
                    },
                    undefined,
                    undefined,
                    { method: 'GET', api: 'node' }
                );
                return this.app.resources.product.getAll(res.result.products || []);
            },
            undefined,
            undefined,
            {
                noCache: !!options?.noCache
            }
        );
    }

    /**
     * Loads model data from hive API
     */
    loadModel<T>(loadRequestParams: ILoadModelRequestParams): Promise<T> {
        if (!this.awaitingLoads.has(loadRequestParams.type)) {
            this.awaitingLoads.set(loadRequestParams.type, new Map());
        }
        const typeMap = this.awaitingLoads.get(loadRequestParams.type)!;
        if (!typeMap.has(loadRequestParams.verbosity)) {
            typeMap.set(loadRequestParams.verbosity, new Map());
        }
        const verbosityMap = typeMap.get(loadRequestParams.verbosity)!;

        if (!verbosityMap.has(loadRequestParams.id)) {
            const entry: IPromiseMethodWrap<T> = {} as IPromiseMethodWrap<T>;
            entry.promise = new Promise<T>((resolve, reject) => {
                entry.resolve = resolve;
                entry.reject = reject;
                verbosityMap.set(loadRequestParams.id, entry);
                this.assureAwaitingLoadsRunningDelayed();
            });
        }

        return verbosityMap.get(loadRequestParams.id)!.promise;
    }

    protected awaitingLoads = new Map<string, Map<number, Map<number | string, IPromiseMethodWrap<any>>>>();

    protected assureAwaitingLoadsRunning() {
        const itemsLimit = 50;
        const itemsLimitOverflow = 70;
        Array.from(this.awaitingLoads.entries()).forEach(([type, verbosityMap]) => {
            Array.from(verbosityMap.entries()).forEach(([verbosity, idMap]) => {
                const ids = Array.from(idMap.keys()).sort();
                while (ids.length > 0) {
                    const idsChunk = ids.splice(0, ids.length > itemsLimitOverflow ? itemsLimit : itemsLimitOverflow);
                    this.send<Nexus.Api.Catalog.Load.Response>(
                        undefined,
                        {
                            path: `load?type=${type}&verbosity=${verbosity}&ids=${idsChunk.join(',')}${RequestService.preventPushingDepsFor.includes(type) ? '' : '&pushDeps=true'}`
                        },
                        undefined,
                        undefined,
                        {
                            method: 'GET',
                            api: 'node'
                        }
                    )
                        .then(res => {
                            const models = keyBy(res.result, 'id');
                            idsChunk.forEach(id => {
                                const model = models[id];
                                const entry = idMap.get(id);
                                if (entry) {
                                    model ? entry.resolve(model) : entry.reject(404);
                                    idMap.delete(id);
                                }
                            });
                        })
                        .catch(error => {
                            idsChunk.forEach(id => {
                                const entry = idMap.get(id);
                                if (entry) {
                                    entry.reject(error);
                                    idMap.delete(id);
                                }
                            });
                        });
                }
            });
        });
        this.awaitingLoads.clear();
    }
    protected assureAwaitingLoadsRunningDelayed = this.timeoutService.throttle(() => this.assureAwaitingLoadsRunning(), 20);

    /**
     * Find model for given path
     */
    async route(request: string | { path: string; pushDeps?: boolean }): Promise<Nexus.Lib.Catalog.RouteResponse['result']> {
        const path = typeof request === 'string' ? request : request.path;
        const data = typeof request !== 'string' && request.pushDeps ? { pushDeps: true } : undefined;
        const response = await this.send<Nexus.Lib.Catalog.RouteResponse>(
            undefined,
            { path: 'route/' + path.replace(/^\//, ''), data },
            undefined,
            undefined,
            {
                method: 'GET',
                api: 'node'
            }
        );
        return response.result;
    }

    /**
     * Same as send but for form
     * Not only sends request but additionally triggers form validation
     *
     * @param  {object} form      Angular form object
     * @param  {int | object} status        Request status as defined in `constat` or status model
     * @param  {object | function} callable      Either function that will return promise or
     * {path : string, data: object} or {fn : function, data: object}
     * @param  {function | function[]} onSuccess  On request success callback
     * @param  {function | function[]} onError    On request error callback
     * @return {promise}             Request promise (can be accessed via getPromsie)
     */
    submitForm<T>(
        form: NgForm | undefined,
        status: Status | undefined,
        callable: ICallable,
        onSuccess?: IRequestCallback<T>,
        onError?: IRequestCallback,
        requestOptions?: IRequestOptions,
        options?: ISubmitFormOptions
    ): Promise<T> | void {
        // mark as submitted
        if (form) {
            form.submitAttempt = true;
            form.ngSubmit.emit();
        }

        // apply validation
        if (!form || form.valid) {
            return this.send(
                status,
                callable,
                <IRequestCallbackFunction[]>compact(union(isArray(onSuccess) ? onSuccess : [onSuccess], [unsubmit])),
                onError,
                requestOptions
            );
        } else if (options?.errorScrollContainer) {
            this.scrollToErrorService.scrollTo(options?.errorScrollContainer, options.errorScrollOptions);
        }

        function unsubmit() {
            if (form) {
                form.submitAttempt = false;
            }
        }
    }

    // will create for willed with data and submit it using post method
    // if target is specified we create iframe dynamically and insert it before form, it won't work in chrome otherwise
    postLikeForm(
        action: string,
        params: any,
        options?: { target?: string; container?: Node; onLoad?: () => any; onError?: (err: any) => any }
    ) {
        const renderer = this.headService.renderer2;
        const form = renderer.createElement('form');
        renderer.setAttribute(form, 'method', 'post');
        renderer.setAttribute(form, 'action', action);
        renderer.addClass(form, 'ng-hide');
        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                const hiddenField = renderer.createElement('input');
                renderer.setAttribute(hiddenField, 'type', 'hidden');
                renderer.setAttribute(hiddenField, 'name', key);
                renderer.setAttribute(hiddenField, 'value', params[key]);
                form.appendChild(hiddenField);
            }
        }

        const container: Node = options?.container || this.document.body;
        renderer.appendChild(container, form);
        if (options?.target) {
            form.setAttribute('target', options.target);
        }

        if (options?.onLoad || options?.onError) {
            const frame = renderer.createElement('iframe');
            const frameName = 'requestframe.' + new Date().getTime();
            renderer.setAttribute(frame, 'name', frameName);
            renderer.setAttribute(form, 'target', frameName);
            renderer.addClass(frame, 'ng-hide');

            if (options.onLoad) {
                frame.onload = () => {
                    renderer.removeChild(container, form);
                    renderer.removeChild(container, frame);
                    options.onLoad!();
                };
            }
            if (options.onError) {
                frame.onerror = (err: any) => {
                    renderer.removeChild(container, form);
                    renderer.removeChild(container, frame);
                    options.onError!(err);
                };
            }
            renderer.appendChild(container, frame);
        }

        if (options?.target) {
            const iframe = renderer.createElement('iframe');
            iframe.name = options.target;
            form.parentNode.insertBefore(iframe, form);
        }
        form.submit();
    }

    /**
     * Register observer
     * Observer function are called on any request success and error
     * Whenever data from BE is previded observers are called
     * @param  {(resultData) => void}   callback
     * @param {number}                  priority
     *
     *
     * @deprecated Use dfEvent with id dfApiRequest instead
     */
    subscribe<TResult = any>(observer: (data?: IResponseMeta<TResult>) => void, priority?: number): Subscription {
        const subscription = this.subject.subscribe(observer);
        if (priority !== undefined) {
            this.prioritizedSubscriptions.push({ subscription, priority });
            this.subject.observers.sort((a, b) => {
                const aPriority = this.getSubscriptionPriority(a as unknown as Subscription);
                const bPriority = this.getSubscriptionPriority(b as unknown as Subscription);
                if (aPriority > bPriority) {
                    return 1;
                }
                if (aPriority < bPriority) {
                    return -1;
                }
                return 0;
            });
        }

        return subscription;
    }

    /**
     * Returns priority for request service subscription
     * @returns {number}
     */
    private getSubscriptionPriority(subscription: Subscription): number {
        const prioritizedSubscription = find(this.prioritizedSubscriptions, proSub => proSub.subscription === subscription);
        return prioritizedSubscription ? prioritizedSubscription.priority * -1 : 0;
    }

    /**
     * Serialises object into get params
     * @param {any} ob
     */
    serialize(ob: ISerializeObject): string {
        return Object.keys(ob)
            .reduce((a: string[], k: string) => {
                if (ob[k] !== undefined) {
                    a.push(k + '=' + encodeURIComponent(ob[k]));
                }
                return a;
            }, [])
            .join('&');
    }

    /**
     * Checks if resource is loaded
     * @param {string} url
     * @returns {boolean}
     */
    isAssetLoaded(url: string): boolean {
        const asset = this.loadedAssets[url];
        return !!asset && asset.loaded;
    }

    /**
     * Checks if resource is loading
     * @param {string} url
     * @returns {boolean}
     */
    isAssetLoading(url: string): boolean {
        const asset = this.loadedAssets[url];
        return !!asset && asset.loading;
    }

    loadScript(url: string, options?: IScriptLoadOptions<HTMLScriptElement>) {
        return this.loadAsset<HTMLScriptElement>(url, options?.onLoad, options?.onError, options?.reload, options, ResourceType.Script);
    }

    async loadScriptAsync(
        url: string,
        options?: Omit<IScriptLoadOptions<HTMLScriptElement>, 'onLoad' | 'onError'>
    ): Promise<HTMLScriptElement> {
        return new Promise<HTMLScriptElement>((resolve, reject) => {
            this.loadScript(url, {
                ...options,
                onLoad: resolve,
                onError: reject
            });
        });
    }

    loadCss(
        url: string,
        onSuccess?: ICallableFunction,
        onError?: ICallableFunction,
        force: boolean = false,
        options?: Omit<IScriptLoadOptions<HTMLLinkElement>, 'onLoad' | 'onError'>
    ) {
        return this.loadAsset<HTMLLinkElement>(url, onSuccess, onError, force, options, ResourceType.Stylesheet);
    }

    async loadCssAsync(
        url: string,
        options?: Omit<IScriptLoadOptions<HTMLLinkElement>, 'onLoad' | 'onError'> & { reload?: boolean }
    ): Promise<HTMLScriptElement> {
        return new Promise<HTMLScriptElement>((resolve, reject) => {
            this.loadCss(url, resolve, reject, options?.reload, options);
        });
    }

    /**
     * Loads js script
     * @param {string} url
     * @param {boolean} force pass true if  we need to reload script
     */
    private loadAsset<TElement extends HTMLScriptElement | HTMLLinkElement>(
        url: string,
        onSuccess?: ICallableFunction,
        onError?: ICallableFunction,
        force: boolean = false,
        options?: IScriptLoadOptions<TElement>,
        type: ResourceType = ResourceType.Script
    ): CancelLoadAssetsCallbacks | void {
        if (!this.loadedAssets[url]) {
            this.loadedAssets[url] = {
                onSuccess: [],
                onError: [],
                loaded: false,
                loading: false
            };
        }
        const asset = this.loadedAssets[url];

        const cancelCallbacks: CancelLoadAssetsCallbacks = () => {
            const onSuccessIndex = onSuccess && asset.onSuccess.indexOf(onSuccess);
            const onErrorIndex = onError && asset.onError.indexOf(onError);
            if (onSuccessIndex && onSuccessIndex > -1) {
                asset.onSuccess.splice(onSuccessIndex, 1);
            }
            if (onErrorIndex && onErrorIndex > -1) {
                asset.onError.splice(onErrorIndex, 1);
            }
        };

        if (asset.loaded && !force) {
            if (onSuccess) {
                onSuccess(asset.target);
            }
            return;
        } else {
            if (onSuccess) {
                asset.onSuccess.push(onSuccess);
            }
            if (onError) {
                asset.onError.push(onError);
            }
        }
        if (asset.loading) {
            return cancelCallbacks;
        }
        asset.loading = true;

        const finalCleanup = () => {
            if (options?.cleanup) {
                this.timeoutService.setTimeout(() => {
                    this.headService.remove(script);
                }, 1000);
            }
        };

        const script = this.loadResource(
            url,
            script => {
                asset.loaded = true;
                asset.loading = false;
                asset.onError = [];
                forEach(asset.onSuccess, cb => cb(script));
                finalCleanup();
            },
            script => {
                asset.loading = false;
                asset.onSuccess = [];
                forEach(asset.onError, cb => cb(script));
                finalCleanup();
            },
            type,
            options
        );

        if (options?.onScript) {
            options?.onScript(script as TElement);
        }
        return cancelCallbacks;
    }

    /**
     * Loads external resource
     * @param {string}                  url - external resource url
     * @param {ICallableFunction}       onSuccess - on load success callback
     * @param {ICallableFunction}       onError - on load error callback
     * @param {ResourceType}    type - external resource type - script is default
     */
    loadResource<TElement extends HTMLScriptElement | HTMLLinkElement = HTMLScriptElement | HTMLLinkElement>(
        url: string,
        onSuccess?: ICallableFunction,
        onError?: ICallableFunction,
        type: ResourceType = ResourceType.Script,
        options?: IScriptLoadOptions<TElement>
    ): TElement {
        let script: TElement;

        switch (type) {
            case ResourceType.Stylesheet:
                script = this.headService.add({
                    element: 'link',
                    ssr: true,
                    attributes: {
                        rel: 'stylesheet',
                        media: 'screen',
                        type: 'text/css',
                        href: url
                    },
                    onLoad: onSuccess ? el => onSuccess(el) : undefined,
                    onError: onError ? el => onError(el) : undefined
                }) as TElement;
                break;
            case ResourceType.Script:
                const scriptAttrs: { [key: string]: string } = {
                    src: url,
                    async: 'true'
                };
                if (options?.scriptType) {
                    scriptAttrs['type'] = options.scriptType;
                }
                script = this.headService.add({
                    element: 'script',
                    ssr: true,
                    attributes: scriptAttrs,
                    onLoad: onSuccess ? el => onSuccess(el) : undefined,
                    onError: onError ? el => onError(el) : undefined
                }) as TElement;
                break;
        }

        return script;
    }
}

declare module '@df/core/app' {
    interface App {
        request: RequestService;
    }
}
